import { useState } from "react";
import {
  REGION_OPTIONS,
  YEAR_OPTIONS,
  GENRE_OPTIONS,
  type MediaFilters,
} from "@/lib/media";

interface FilterSidebarProps {
  filters: MediaFilters;
  onToggleRegion: (value: string) => void;
  onToggleYear: (value: number) => void;
  onToggleGenre: (value: string) => void;
  onReset: () => void;
}

interface ChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function Chip({ label, active, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs md:text-sm border whitespace-nowrap transition-colors cursor-pointer ${
        active
          ? "bg-primary-500 border-primary-500 text-background-50"
          : "bg-background-50 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterSidebar({
  filters,
  onToggleRegion,
  onToggleYear,
  onToggleGenre,
  onReset,
}: FilterSidebarProps) {
  const [open, setOpen] = useState(false);

  const activeCount =
    filters.regions.length + filters.years.length + filters.genres.length;

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-4 border-b border-background-200">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-primary-600">
              <i className="ri-filter-3-line"></i>
            </span>
            <span className="text-sm font-medium text-foreground-900">
              筛选条件
              {activeCount > 0 && (
                <span className="ml-1 text-primary-600">({activeCount})</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={onReset}
                className="text-xs text-foreground-500 hover:text-accent-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                重置
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="展开筛选"
              className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer"
            >
              <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          </div>
        </div>

        <div className={`${open ? "block" : "hidden"} lg:block px-4 py-4 space-y-6`}>
          <div>
            <h4 className="text-xs font-label tracking-wide text-foreground-400 mb-3">地区</h4>
            <div className="flex flex-wrap gap-2">
              {REGION_OPTIONS.map((region) => (
                <Chip
                  key={region}
                  label={region}
                  active={filters.regions.includes(region)}
                  onClick={() => onToggleRegion(region)}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-label tracking-wide text-foreground-400 mb-3">年份</h4>
            <div className="flex flex-wrap gap-2">
              {YEAR_OPTIONS.map((year) => (
                <Chip
                  key={year}
                  label={`${year}`}
                  active={filters.years.includes(year)}
                  onClick={() => onToggleYear(year)}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-label tracking-wide text-foreground-400 mb-3">类型</h4>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => (
                <Chip
                  key={genre}
                  label={genre}
                  active={filters.genres.includes(genre)}
                  onClick={() => onToggleGenre(genre)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}